<template>
	<div class="h-full">
		<div class="relative h-full flex flex-col">
			<!-- TAB MENU -->
			<div class="sticky px-2 mb-6 top-0 bg-base z-10">
				<div class="flex justify-between items-center">
					<div class="tabs tabs-lg tabs-bordered justify-start flex-nowrap" role="tablist">
						<a
							class="tab leading-5"
							:class="[
								activeTab === 'content' ? 'tab-active z-30' : 'text-base-content-light',
								invalidTitleError ? 'text-red-500' : '',
							]"
							@click.prevent="activeTab = 'content'"
						>
							{{ $t("Content") }}
						</a>

						<a
							class="tab leading-5"
							role="tab"
							:class="[activeTab === 'supplies' ? 'tab-active z-30' : 'text-base-content-light']"
							@click.prevent="activeTab = 'supplies'"
						>
							{{ $t("Supplies") }}
							<span class="badge badge-sm ml-1 badge-info" v-if="lessonPlan?.supplies?.length > 0">{{
								lessonPlan.supplies.length
							}}</span>
						</a>

						<a
							class="tab leading-5"
							role="tab"
							:class="[activeTab === 'goals' ? 'tab-active z-30' : 'text-base-content-light']"
							@click.prevent="openGoalsTab"
						>
							{{ $tc("Goal", 2) }}
							<span class="badge badge-sm ml-1 badge-info" v-if="lessonPlan?.goals?.length > 0">{{
								countParentGoals(lessonPlan.goals || [])
							}}</span>
						</a>
						<a
							class="tab leading-5"
							role="tab"
							:class="[activeTab === 'attachments' ? 'tab-active z-30' : 'text-base-content-light']"
							@click.prevent="activeTab = 'attachments'"
							>{{ $tc("Attachments", 2) }}
							<span class="badge badge-sm ml-1 badge-info" v-if="lessonPlan?.attachments?.length > 0">{{
								lessonPlan.attachments.length
							}}</span>
						</a>
					</div>
				</div>
				<!-- Underline -->
				<div class="border-base-200 border-b-2 absolute bottom-0 left-0 right-0 z-20" />
			</div>

			<!-- TAB PANELS -->
			<div class="flex flex-col flex-1 min-h-0">

				<!-- CONTENT TAB -->
				<div v-show="activeTab === 'content'" class="flex flex-col flex-1 min-h-0 pb-24">
					<div class="overflow-hidden flex-1 min-h-0 font-medium rounded flex flex-col">
						<input
							class="input mb-2 border-b"
							:class="[invalidTitleError ? 'border-red-500' : '']"
							maxlength="128"
							name="title"
							id="title"
							:placeholder="$t('Title')"
							autofocus
							ref="titleField"
							v-model="lessonPlan.title"
						/>
						<TextEditor
							class="flex-1 min-h-0"
							:placeholder="$t('Description') + '...'"
							v-model="lessonPlan.instructions"
						/>
					</div>

					<!-- <div v-if="showOptionGuided" class="flex items-center gap-2">
						<input
							type="checkbox"
							class="checkbox checkbox-sm"
							v-model.lazy="lessonPlan.isGuided"
							name="guided"
							id="guided-activity"
						/>
						<label for="guided-activity" class="flex gap-2">
							<span class="text-sm mt-0.5">{{ $t("Under guidance of the teacher") }}</span>
							<LifebuoyIcon class="w-6 h-6" />
						</label>
					</div> -->
				</div>

				<!-- SUPPLIES TAB -->
				<div v-show="activeTab === 'supplies'" class="px-6 pb-24">
					<ListEditor
						v-if="activeTab === 'supplies' && lessonPlan"
						v-model="lessonPlan.supplies"
					/>
				</div>

				<!-- GOALS TAB -->
				<div v-show="activeTab === 'goals'" class="pb-24">
					<!-- <template v-if="!organisationsStore.currentOrganisation.curriculum">
						<p class="text-base-content-light pt-4 italic text-sm text-center">
							{{ $t("No goals available yet") }}
						</p>
					</template>

					<template v-else> -->
						<div class="px-6 pb-4">
							<GoalSelectorBar
								:context="goalSourceContext"
								:scope="goalScope"
								:selectedGoals="lessonPlan.goals || []"
								:weekGoals="suggestedGoals"
								:launchPending="goalLaunchPending"
								@openCurriculum="launchGoalSource({ curriculum: $event })"
								@openCollection="launchGoalSource($event)"
								@goalSelected="addGoal"
								@goalUnselected="removeGoal"
							/>
						</div>

						<div
							v-show="!lessonPlan?.goals?.length"
							class="text-base-content-light text-sm italic text-center mb-6"
						>
							{{ $t("No goals selected") }}
						</div>

						<GoalsField
							:curriculum="organisationsStore.getCurrentOrganisation().curriculum"
							:selectedGoals="lessonPlan.goals || []"
							:suggestedGoals="suggestedGoals"
							context="lessonPlan"
							@goalSelected="addGoal"
							@goalUnselected="removeGoal"
							@goalMuteToggled="toggleGoalMuted"
						/>
					<!-- </template> -->
				</div>

				<!-- ATTACHMENTS TAB -->
				<div v-show="activeTab === 'attachments'" class="pb-24">
					<Dropzone @files-dropped="filesDropped($event)" class="h-full">
						<Attachments
							v-model="lessonPlan.attachments"
							v-model:uploading="attachmentsBusy"
							:preview="false"
							ref="attachmentsContainer"
							accept="*"
							:header="false"
							:uploadButton="true"
							:externalFilesButton="true"
						/>
					</Dropzone>
				</div>
			</div>

			<component
				v-if="activeGoalLaunch?.kind === 'external' && lessonPlan"
				:is="activeGoalLaunch.component"
				context="lessonPlan"
				:selectedGoals="lessonPlan.goals || []"
				:suggestedGoals="suggestedGoals"
				@goalSelected="addGoal"
				@goalUnselected="removeGoal"
				@opened="goalLaunchPending = false"
				@requestUnmount="activeGoalLaunch = null; goalLaunchPending = false"
			/>

			<GoalSelector
				v-else-if="activeGoalLaunch?.kind === 'drawer' && lessonPlan"
				:curriculum="activeGoalLaunch.curriculum"
				:fixedSource="activeGoalLaunch.fixedSource"
				:selectedGoals="lessonPlan.goals || []"
				@goalSelected="addGoal"
				@goalUnselected="removeGoal"
				@requestUnmount="activeGoalLaunch = null"
			/>
		</div>
	</div>
</template>


<script lang="ts">
import ListEditor from "@/components/ui/ListEditor.vue";
import TextEditor from "@/components/ui/TextEditor.vue";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import Attachments from "@/components/Attachments.vue";
import Dropzone from "@/components/Dropzone.vue";
import GoalSelector from "@/components/GoalSelector.vue";
import GoalsField from "@/components/GoalsField.vue";
import GoalSelectorBar from "@/components/GoalSelectorBar.vue";
import { GoalPickerLaunchDescriptor, GoalPickerLaunchSource, resolveGoalPickerLaunch } from "@/goals/utils/GoalPickerLaunch.util";

import { Goal } from "@/models/Goal.model";
import { countParentGoals } from "@/models/Curriculum.model";
import { useGoalsStore } from "@/stores/Goals.store";
import { mapStores } from "pinia";

export default {
	emits: ["form:close"],

	props: {
		lessonPlan: {
			type: Object as any,
			required: true,
		},
		showOptionGuided: {
			type: Boolean,
			default: false,
		},
		errors: {
			type: Object,
			default: () => ({}),
		},
		suggestedGoals: {
			type: Array,
			default: () => [],
		},
		focusOnError: {
			type: Boolean,
			default: true,
		},
		goalSourceContext: {
			type: String,
			default: 'lessonPlan',
			validator: (value: string) => ['lessonPlan', 'agendaLessonPlan'].includes(value),
		},
		// 'global' (library): offer every curriculum of the user's schools.
		// 'school' (agenda): only the current school's curriculum.
		goalScope: {
			type: String,
			default: 'global',
			validator: (value: string) => ['global', 'school'].includes(value),
		},
	},

	components: {
		Dropzone,
		Attachments,
		ListEditor,
		TextEditor,
		GoalSelector,
		GoalsField,
		GoalSelectorBar,
	},

	data() {
		return {
			activeTab: "content",
			attachmentsBusy: false,
			activeGoalLaunch: null as GoalPickerLaunchDescriptor | null,
			// True from the click until the external picker reports `opened` (or gives up
			// and unmounts). Only external launches set it — drawer launches open instantly.
			goalLaunchPending: false,
		};
	},
	computed: {
		...mapStores(useOrganisationsStore, useGoalsStore),

		invalidTitleError(): boolean {
			return Boolean(this.errors?.fields?.title && !this.lessonPlan?.title);
		},
	},

	async mounted() {
		if ((this as any).tab) {
			this.activeTab = (this as any).tab;
		}
	},

	watch: {
		errors: {
			handler(newValue: any) {
				if (newValue && newValue.fields && newValue.fields.title) {
					this.openContentTab(this.focusOnError ? "title" : null);
				}
			},
			deep: true,
		},
	},

	methods: {
		countParentGoals,

		close() {
			this.$emit("form:close");
		},

		filesDropped(files: any) {
			(this.$refs.attachmentsContainer as any).filesDropped(files);
		},

		openContentTab(focus: string | null = null) {
			this.activeTab = "content";
			if (focus === "title") {
				setTimeout(() => (this.$refs.titleField as any)?.focus(), 500);
			}
		},

		openGoalsTab() {
			this.activeTab = "goals";
		},

		launchGoalSource(source: GoalPickerLaunchSource) {
			try {
				const descriptor = resolveGoalPickerLaunch(source);
				this.goalLaunchPending = descriptor.kind === 'external';
				this.activeGoalLaunch = descriptor;
			} catch (e) {
				console.error('Could not launch goal selector', e);
				this.$notify({ text: this.$t('An error occurred'), type: 'error' });
			}
		},

		addGoal(goal: Goal) {
			if (!this.lessonPlan) return;
			if (!this.lessonPlan.goals) this.lessonPlan.goals = [];
			const goals = this.lessonPlan.goals;

			const existing = goals.find((item: any) =>
				this.goalsStore.representsSameModel(item, goal)
			);
			if (existing) {
				// Picked again: only the link state can change. Clone-and-replace so the
				// shared original instance is not mutated (see toggleGoalMuted).
				if (existing.muted !== goal.muted) {
					const updated = existing.clone();
					updated.muted = goal.muted;
					goals.splice(goals.indexOf(existing), 1, updated);
				}
				return;
			}
			goals.push(goal);
		},

		toggleGoalMuted(goal: Goal) {
			const goals = this.lessonPlan?.goals;
			if (!goals) return;
			const index = goals.indexOf(goal);
			if (index === -1) return;
			// Clone-and-replace: the Goal instance is shared by reference with the
			// original model (LessonPlan.clone() copies the array shallowly), so an
			// in-place mutation would edit the original too and blind the
			// unsaved-changes comparison. A fresh instance keeps the original intact.
			const toggled = goal.clone();
			toggled.muted = !goal.muted;
			goals.splice(index, 1, toggled);
		},

		removeGoal(goal: Goal) {
			if (!this.lessonPlan?.goals) return;

			this.lessonPlan.goals = this.lessonPlan.goals.filter((item: any) => {
				// Remove children if goal has sourcePathId
				if (goal.sourcePathId && item.sourceParentPath === goal.fullPath) {
					return false;
				}
				// Remove the goal itself
				return !this.goalsStore.representsSameModel(item, goal);
			});
		},
	},
};
</script>
